import { NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { ChangeDetectionStrategy, Component, Input, OnInit, Optional, Self } from '@angular/core';
import { DsAbstractControlComponent } from '@abstract/components/ds-abstract-control.component';
import { DateAdapter, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS, MatNativeDateModule, NativeDateAdapter } from '@angular/material/core';

/**
 * `DsDatepickerComponent` is a standalone Angular component that wraps a Material
 *  Datepicker and integrates it with Angular Reactive Forms. It extends
 * `DsAbstractControlComponent` and can be used as a form control.
 *
 * @example
 * <ds-datepicker [min]="'2026-01-01'" [max]="'2026-12-31'" formControlName="date" label="Date"></ds-datepicker>
 */
@Component({
  selector: 'ds-datepicker',
  templateUrl: './ds-datepicker.component.html',
  styleUrls: ['./ds-datepicker.component.scss'],
  standalone: true,
  imports: [NgIf, MatIconModule, MatInputModule, ReactiveFormsModule, MatDatepickerModule, MatNativeDateModule],
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsDatepickerComponent extends DsAbstractControlComponent implements OnInit {
  /**
   * Minimum allowed date in ISO string format (`YYYY-MM-DD`).
   */
  @Input() min?: string | Date;

  /**
   * Maximum allowed date in ISO string format (`YYYY-MM-DD`).
   */
  @Input() max?: string | Date;

  /**
   * When true, shows a clear (close) icon in place of the datepicker toggle while a value is selected.
   * Clicking it resets the control to `null`.
   */
  @Input() isClearable = false;

  /** Whether the bound control currently has a non-empty value */
  hasSelection = false;

  /**
   * Disabled state of the datepicker.
   */
  disabled = false;

  constructor(@Self() @Optional() public ngControl: NgControl) {
    super();

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.syncHasSelection(this.control?.value);
    this.subscribeOnControlValueChanges();
  }

  /**
   * Clears the current value without opening the datepicker.
   *
   * @param event - mouse event from the clear icon
   */
  onClearSelection(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.disabled || !this.control || !this.hasSelection) {
      return;
    }

    this.control.setValue(null);
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.onTouched();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Empty because formControl handles everything, but required for ControlValueAccessor
   */
  writeValue(): void {}

  /**
   * Sets the disabled state of the component from Angular forms.
   *
   * @param isDisabled - `true` if the component should be disabled, `false` otherwise
   */
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Keeps `hasSelection` in sync with the bound form control value.
   */
  private subscribeOnControlValueChanges(): void {
    if (!this.control) {
      return;
    }

    const subscription = this.control.valueChanges.subscribe((value: unknown): void => {
      this.syncHasSelection(value);
      this.changeDetectorRef.markForCheck();
    });

    this.subscriptions.add(subscription);
  }

  /**
   * Updates whether a clearable value is currently present.
   *
   * @param value - current control value
   */
  private syncHasSelection(value: unknown): void {
    this.hasSelection = value !== null && value !== undefined && value !== '';
  }

  /**
   * Convenience getter to access the underlying `FormControl`.
   *
   * @returns The `FormControl` instance associated with this component.
   */
  get control(): FormControl {
    return this.ngControl?.control as FormControl;
  }
}
